import React, { useEffect, useState, useRef } from 'react';
import { Trophy, ShieldX, ShieldCheck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTour } from '@reactour/tour';
import SwipeCard from './../components/SwipeCard';
import FeedbackCard from './../components/FeedbackCard';
import emailTemplatesNL from './../data/emailTemplatesNL';
import emailTemplatesEN from './../data/emailTemplatesEN';
import emailTemplatesFR from './../data/emailTemplatesFR';
import feedbackTemplatesNL from './../data/feedbackTemplatesNL';
import feedbackTemplatesEN from './../data/feedbackTemplatesEN';
import feedbackTemplatesFR from './../data/feedbackTemplatesFR';
import translations from './../data/translations';
import { AnimatePresence, motion } from 'framer-motion';


function Home() {
  const navigate = useNavigate();
  const [emails, setEmails] = useState([]);
  const [score, setScore] = useState(0);
  const [forcedDirection, setForcedDirection] = useState(null);
  const [language, setLanguage] = useState(localStorage.getItem('language') || 'nl');
  const [feedbackId, setFeedbackId] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [finalPopup, setFinalPopup] = useState(false);

 const t = translations[language] || translations.en;
 const templatesByLang = { nl: emailTemplatesNL, en: emailTemplatesEN, fr: emailTemplatesFR };
 const feedbackByLang = { nl: feedbackTemplatesNL, en: feedbackTemplatesEN, fr: feedbackTemplatesFR };
 const templates = templatesByLang[language] || emailTemplatesEN;
 const feedbackSet = feedbackByLang[language] || feedbackTemplatesEN;
  const feedbackEmail = feedbackSet.find((f) => f.id === feedbackId);

  const totalEmails = templates.length;
  const swipeCardRef = useRef(null);

  // Last challenge timing state
  const [showLastPopup, setShowLastPopup] = useState(false);
  const [lastStartIso, setLastStartIso] = useState(localStorage.getItem('lastStartIso') || null);
  const [lastChallengeArmed, setLastChallengeArmed] = useState(false); // true once user clicks Start

  const [hasSeenTour, setHasSeenTour] = useState(localStorage.getItem('seenTour') === 'true');
  const [showWelcomePopup, setShowWelcomePopup] = useState(false);

  const { setIsOpen, setSteps, isOpen } = useTour();
  const nickname = localStorage.getItem('nickname') || 'Anonymous';

  // --- Missed-legit toast state ---
  const [missedLegitMsg, setMissedLegitMsg] = useState(null);

  // Professional, localized message
 const missedLegitMessage = t.missedLegit;

  const showMissedLegitToast = () => {
    setMissedLegitMsg(missedLegitMessage);
    // auto-hide after ~2.2s
    setTimeout(() => setMissedLegitMsg(null), 2200);
  };


  useEffect(() => {
    if (!nickname) navigate('/');
  }, [navigate, nickname]);

  useEffect(() => {
    if (localStorage.getItem('hasPlayed') === 'true') {
      navigate('/scoreboard');
    }
  }, [navigate]);

  useEffect(() => {
    const progress = JSON.parse(localStorage.getItem('progress') || 'null');
    if (progress && Array.isArray(progress.remainingIds)) {
      const remaining = templates.filter((t) => progress.remainingIds.includes(t.id));
      setEmails(remaining);
      setScore(progress.score || 0);
    } else {
      setEmails(templates);
      setScore(0);
      // Show welcome popup only when starting a fresh game
      if (templates.length > 0 && !localStorage.getItem('welcomeShown')) {
        // Check if tour should block the welcome popup
        if (hasSeenTour && !isOpen) {
          setTimeout(() => setShowWelcomePopup(true), 500); // Small delay for better UX
        }
      }
    }
  }, [language, hasSeenTour, isOpen]); // rehydrate when language changes

  useEffect(() => {
    setSteps([
      {
        selector: 'header select',
        content: t.selectLanguage,
      },
      {
        selector: 'header a',
        content: t.scoreboard,
      },
      {
        selector: '.SwipeCard',
        content:
          t.tourSwipe ||
          `Swipe left for ${t.phishing.toLowerCase()}, right for ${t.safe.toLowerCase()}. Or use the buttons below!`,
      },
      {
        selector: '.force-buttons',
        content: t.tourButtons || 'Or use these buttons if you prefer.',
      },
      {
        selector: '.score-display',
        content: t.tourScoreDisplay || 'This shows your current score.',
      },
    ]);
  }, [setSteps, t]);

  useEffect(() => {
    if (!hasSeenTour && swipeCardRef.current && !showFeedback) {
      setIsOpen(true);
      localStorage.setItem('seenTour', 'true');
      setHasSeenTour(true);
    }
  }, [emails, showFeedback, setIsOpen, hasSeenTour]);

  // Show welcome popup when tour is closed (if applicable)
  useEffect(() => {
    if (hasSeenTour && isOpen === false) {
      // Tour was closed, check if welcome popup should show
      const progress = JSON.parse(localStorage.getItem('progress') || 'null');
      if (!progress && !localStorage.getItem('welcomeShown')) {
        setTimeout(() => setShowWelcomePopup(true), 300);
      }
    }
  }, [isOpen, hasSeenTour]);

  // Open the "Final challenge" popup when exactly 1 email remains and we have not armed it yet
  useEffect(() => {
    if (!showFeedback && emails.length === 1 && !lastChallengeArmed) {
      setShowLastPopup(true);
    }
  }, [emails, showFeedback, lastChallengeArmed]);

  const handleSwipe = async (direction, email) => {
    const userAnswer = direction === 'left';
    const isCorrect = userAnswer === email.isPhishing;

    // Was this the last card before removal?
    const isLastCardNow = emails.length === 1;

    const isMissedLegit = !isCorrect && !email.isPhishing; // user said phishing, but it was legit

    // If the user incorrectly marked a legitimate email as phishing:
    if (isMissedLegit) {
      // show centered toast and delay removal to avoid overlapping white card behind it
      showMissedLegitToast();
      setForcedDirection(null);

      // Remove the card and progress AFTER the toast fades (~2.2s)
      setTimeout(async () => {
        const updatedScore = score; // no increment
        const updatedEmails = emails.filter((e) => e.id !== email.id);
        const remainingIds = updatedEmails.map((e) => e.id);
        const apiUrl = window.APP_CONFIG?.apiBaseUrl;

        setEmails(updatedEmails);
        setScore(updatedScore);
        localStorage.setItem('progress', JSON.stringify({ remainingIds, score: updatedScore }));

        if (updatedEmails.length === 0) {
          localStorage.setItem('hasPlayed', 'true');
          localStorage.removeItem('progress');
          setFinalPopup(true);
          try {
            const lastStartedAt = localStorage.getItem('lastStartIso') || null;
            await fetch(`${apiUrl}/api/score`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'X-Game-Token': 'b73ebff9-4537-477b-97f5-ebb968bfbe40',
              },
              body: JSON.stringify({ nickname, score: updatedScore, lastStartedAt }),
            });
          } catch (error) {
            console.error('Failed to submit score:', error);
          } finally {
            localStorage.removeItem('lastStartIso');
            setLastStartIso(null);
            setLastChallengeArmed(false);
          }
        }
      }, 2200);
      return;
    }

    // If this is the last card, capture finish timing right away
    if (isLastCardNow) {
      const apiUrl = window.APP_CONFIG?.apiBaseUrl;
      const finishedAt = new Date().toISOString();
      const startedAt = lastStartIso || finishedAt; // fallback if start was not set for any reason

      try {
        await fetch(`${apiUrl}/api/last-challenge/finish`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Game-Token': 'b73ebff9-4537-477b-97f5-ebb968bfbe40',
          },
          body: JSON.stringify({
            nickname,
            templateId: email.id,
            startedAt,
            finishedAt,
          }),
        });
      } catch (err) {
        console.error('Failed to record last challenge finish:', err);
      }
      // Do NOT clear lastStartIso here. We need it for /api/score. We will clear it after score submit.
    }

    const updatedScore = isCorrect ? score + 1 : score;
    const updatedEmails = emails.filter((e) => e.id !== email.id);
    const remainingIds = updatedEmails.map((e) => e.id);
    const apiUrl = window.APP_CONFIG?.apiBaseUrl;

    setEmails(updatedEmails);
    setScore(updatedScore);
    setForcedDirection(null);

    localStorage.setItem(
      'progress',
      JSON.stringify({
        remainingIds,
        score: updatedScore,
      })
    );

    if (!isCorrect && email.isPhishing) {
      setFeedbackId(email.id);
      setShowFeedback(true);
      return;
    }

    if (updatedEmails.length === 0) {
      localStorage.setItem('hasPlayed', 'true');
      localStorage.removeItem('progress');
      setFinalPopup(true);
      try {
        const lastStartedAt = localStorage.getItem('lastStartIso') || null;
        await fetch(`${apiUrl}/api/score`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Game-Token': 'b73ebff9-4537-477b-97f5-ebb968bfbe40',
          },
          body: JSON.stringify({ nickname, score: updatedScore, lastStartedAt }),
        });
      } catch (error) {
        console.error('Failed to submit score:', error);
      } finally {
        // Clear timing markers now that score is stored
        localStorage.removeItem('lastStartIso');
        setLastStartIso(null);
        setLastChallengeArmed(false);
      }
    }
  };

  const handleStartLastChallenge = async () => {
    const apiUrl = window.APP_CONFIG?.apiBaseUrl;
    const templateId = emails?.[0]?.id; // the last remaining card
    const startedAt = new Date().toISOString();

    setShowLastPopup(false);
    setLastChallengeArmed(true);
    setLastStartIso(startedAt);
    localStorage.setItem('lastStartIso', startedAt);

    try {
      await fetch(`${apiUrl}/api/last-challenge/start`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Game-Token': 'b73ebff9-4537-477b-97f5-ebb968bfbe40',
        },
        body: JSON.stringify({ nickname, templateId, startedAt }),
      });
    } catch (err) {
      console.error('Failed to record last challenge start:', err);
    }
  };

  return (
    <div className="min-h-screen bg-[#0d1b2a] flex flex-col text-white transition-all duration-300 ease-in-out relative overflow-hidden">
      {/* Gradient Blobs */}
      <div className="absolute inset-0 z-0 overflow-hidden">
        <div className="absolute w-72 h-72 bg-[#00aca9] opacity-20 rounded-full blur-3xl top-10 left-[-100px]" />
        <div className="absolute w-80 h-80 bg-[#f68d43] opacity-20 rounded-full blur-3xl bottom-10 right-[-80px]" />
      </div>

      {/* Header */}
      <header className="sticky top-0 bg-[#0d1b2a] w-full px-4 sm:px-6 py-4 pb-2 z-30 bg-transparent mt-4">
        <div className="relative flex items-center justify-between max-w-6xl mx-auto">
          <select
            value={language}
            onChange={(e) => {
              const selected = e.target.value;
              localStorage.setItem('language', selected);
              setLanguage(selected);
            }}
            className="bg-black/20 border border-white/50 text-white px-3 py-1 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#00aca9] transition"
          >
            <option value="nl">NL</option>
            <option value="en">EN</option>
            <option value="fr">FR</option>
          </select>

          <div className="absolute left-1/2 transform -translate-x-1/2">
            <img src="/logo.png" alt="Logo" className="h-16" />
          </div>

          <div className="flex items-center gap-2 sm:gap-4">
            <div className="bg-black/20 border border-white/30 rounded-lg px-2 sm:px-3 py-1 text-xs sm:text-sm text-white/90">
              {nickname}
            </div>
            <a href="/scoreboard">
              <Trophy className="h-6 w-6 sm:h-7 sm:w-7 text-white" />
            </a>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex flex-col items-center px-4 z-10 w-full overflow-hidden">
        <div className="flex flex-col items-center justify-center w-full max-w-full flex-1 overflow-y-auto no-scrollbar p-4">
          <div className="score-display text-white/90 text-sm font-medium bg-black/30 px-4 py-2 rounded-full backdrop-blur-sm mb-4 mt-2">
            Score: {score}/{totalEmails - emails.length}
          </div>

          <div className="w-full max-w-[500px] flex items-center justify-center overflow-visible mb-6">
            {showFeedback && feedbackEmail ? (
              <FeedbackCard
                key={`${feedbackId}-${language}`}
                email={feedbackEmail}
                t={t}
                onClose={async () => {
                  setShowFeedback(false);
                  setFeedbackId(null);

                  if (emails.length === 0) {
                    localStorage.setItem('hasPlayed', 'true');
                    localStorage.removeItem('progress');

                    const apiUrl = window.APP_CONFIG?.apiBaseUrl;

                    try {
                      const lastStartedAt = localStorage.getItem('lastStartIso') || null;
                      await fetch(`${apiUrl}/api/score`, {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json',
                          'X-Game-Token': 'b73ebff9-4537-477b-97f5-ebb968bfbe40',
                        },
                        body: JSON.stringify({ nickname, score, lastStartedAt }),
                      });
                    } catch (err) {
                      console.error('Failed to submit score:', err);
                    } finally {
                      localStorage.removeItem('lastStartIso');
                      setLastStartIso(null);
                      setLastChallengeArmed(false);
                    }

                    setFinalPopup(true);
                  }
                }}
              />
            ) : emails.length > 0 ? (
              // If we are at last card, show popup first
              emails.length === 1 && showLastPopup ? (
                <div className="bg-white text-[#2b3b4b] rounded-xl p-6 max-w-sm w-full text-center shadow-2xl">
                  <h2 className="text-lg font-bold mb-3">
                    {t.lastSwipeTitle || 'Final challenge'}
                  </h2>
                  <p className="text-sm mb-6">
                    {t.lastSwipeBody ||
                      'This is your last swipe. For this one the rules are a bit different. In addition to right or wrong, your reaction time will be measured.'}
                  </p>
                  <button
                    onClick={handleStartLastChallenge}
                    className="bg-[#00aca9] hover:bg-[#00918d] text-white px-5 py-2.5 rounded-lg font-semibold text-sm transition"
                  >
                    {t.startNow || 'Start'}
                  </button>
                </div>
              ) : (
                <SwipeCard
                  key={emails[0].id}
                  email={emails[0]}
                  onSwipe={handleSwipe}
                  forcedDirection={forcedDirection}
                  receiverEmail={nickname}
                  innerRef={swipeCardRef}
                />
              )
            ) : (
              <p className="text-lg text-white/80 text-center">
                {t.noMoreEmails || 'No more emails!'}
              </p>
            )}
          </div>

          {emails.length > 0 && !showFeedback && !showLastPopup && (
            <div className="flex justify-center gap-6 mb-2 force-buttons">
              <button
                onClick={() => setForcedDirection('left')}
                className="flex items-center gap-2 bg-red-600/90 hover:bg-red-700 text-white font-semibold py-3 px-6 rounded-full text-base shadow-md transform hover:scale-105 transition"
              >
                <ShieldX className="w-4 h-4" />
                {t.phishing}
              </button>
              <button
                onClick={() => setForcedDirection('right')}
                className="flex items-center gap-2 bg-[#00aca9]/90 hover:bg-teal-600 text-white font-semibold py-3 px-6 rounded-full text-base shadow-md transform hover:scale-105 transition"
              >
                <ShieldCheck className="w-4 h-4" />
                {t.safe}
              </button>
            </div>
          )}
        </div>

        {finalPopup && (
          <div className="fixed inset-0 bg-[#0d1b2a]/80 backdrop-blur-sm flex items-center justify-center z-50">
            <div className="bg-white text-[#2b3b4b] rounded-xl p-8 max-w-sm w-full text-center shadow-2xl">
              <h2 className="text-xl font-bold mb-4">{t.allSwipesDone}</h2>
              <p className="mb-6">
                {t.popupScored || 'You scored'} <strong>{score}/{totalEmails}</strong>.
              </p>
              <button
                onClick={() => navigate('/scoreboard')}
                className="bg-[#00aca9] hover:bg-[#00918d] text-white px-6 py-3 rounded-lg font-semibold text-base transition"
              >
                {t.viewScoreboard}
              </button>
            </div>
          </div>
        )}
      </main>

      <footer className="text-xs text-white/60 py-4 text-center z-10">
        🚀 <strong className="text-[#f68d43]">Swishing</strong> – {t.footer}
      </footer>

{/* Welcome popup */}
<AnimatePresence>
  {showWelcomePopup && (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      className="fixed inset-0 z-[70] flex items-center justify-center px-4"
    >
      {/* Background overlay */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      
      {/* Popup content */}
      <motion.div
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: 20 }}
        transition={{ duration: 0.3, ease: "easeOut" }}
        className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 text-center"
      >
        <div className="mb-4">
          <div className="w-16 h-16 bg-[#00aca9] rounded-full flex items-center justify-center mx-auto mb-4">
            <ShieldCheck className="w-8 h-8 text-white" />
          </div>
 <h2 className="text-2xl font-bold text-[#2b3b4b] mb-2">
   {t.welcomeTitle}
 </h2>
          <p className="text-gray-600 mb-4">{t.welcomePlayingAs}</p>
          <div className="bg-gray-50 border-2 border-[#00aca9] rounded-lg p-4 mb-6">
            <span className="text-xl font-bold text-[#2b3b4b]">{nickname}</span>
          </div>
          <p className="text-sm text-gray-500 mb-6">{t.welcomePrivacy}</p>
        </div>
        
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => {
            setShowWelcomePopup(false);
            localStorage.setItem('welcomeShown', 'true');
          }}
          className="w-full bg-[#00aca9] hover:bg-[#00918d] text-white font-semibold py-3 px-6 rounded-lg shadow-md transition-colors"
        >
          {t.welcomeStartCTA}
        </motion.button>
      </motion.div>
    </motion.div>
  )}
</AnimatePresence>

{/* Missed-legit toast (center, subtle scrim; does NOT block input) */}
<AnimatePresence>
  {missedLegitMsg && (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="fixed inset-0 z-[60] pointer-events-none"
    >
      {/* faint background separation */}
      <div className="absolute inset-0 bg-black/10 backdrop-blur-[1px]" />
      {/* toast content */}
      <div className="absolute inset-0 flex items-center justify-center px-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.96 }}
          transition={{ duration: 0.22 }}
          className="
            pointer-events-none
            rounded-xl px-5 py-4
            bg-white text-[#2b3b4b] shadow-2xl
            border border-white/70 ring-1 ring-[#00aca9]/30
            text-base sm:text-lg font-semibold
            max-w-md w-full text-center
          "
          role="status"
          aria-live="polite"
        >
          {missedLegitMsg}
        </motion.div>
      </div>
    </motion.div>
  )}
</AnimatePresence>



    </div>
  );
}

export default Home;
